import { createContext, useState, useEffect } from "react";
import { createCart, getCart, patchCart, deleteCart, patchCartQuantity } from "./api/cart";
import { getDesigns } from "./api/misc";
import { getSettings } from "./api/settings";
import { useAuth0 } from "@auth0/auth0-react";
import toast from "react-hot-toast";
import { serverURL } from "./utils";
import axios from "axios";

export const DataContext = createContext({
  customer: null,
  availableProducts: [],
  visitedPages: [],
  addVisitedPage: [],
  designs: [],
  settings: {},
  productsInCart: [],
  getCustomer: () => {},
  getProduct: () => {},
  deleteFromCart: () => {},
  clearCartState: () => {},
  addToCart: () => {},
  updateQuantityFromCart: () => {},
  getSubTotal: () => {},
});

export function DataProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [cartProducts, setCartProducts] = useState([]);
  const [visited, setVisited] = useState({});
  const [designs, setDesigns] = useState([]);
  const [configuration, setConfiguration] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const getAuthHeaders = async () => {
    const token = await getAccessTokenSilently();
    return {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    };
  };

  const addVisitedPage = (page) => {
    let pageVisits;
    if (visited[page]) {
      pageVisits = visited[page] + 1;
    } else {
      pageVisits = 1;
    }
    setVisited({ ...visited, [page]: pageVisits });
  };

  const getProducts = async () => {
    const response = await fetch(`${serverURL()}/products`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }
    const products = await response.json();
    const mappedProducts = products.map((product) => {
      return {
        active: product.active,
        productId: product.id,
        priceId: product.default_price,
        name: product.name,
        description: product.description,
        category: product.metadata.category,
        cost: product.prices[0].unit_amount / 100,
        images: product.images,
      };
    });
    return mappedProducts;
  };

  const linkAccounts = async () => {
    try {
      const token = await getAccessTokenSilently();

      const res = await axios.post(
        `${serverURL()}/user/link-user`,
        {
          passwordlessUserId: user.sub,
          email: user.email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Linking successful:", res.data);
    } catch (err) {
      console.error("Linking failed:", err.response?.data || err.message);
    }
  };

  const getCustomer = async () => {
    const headers = await getAuthHeaders();

    let userId = user.sub;

    console.log("bobtest userID", userId);

    if (user && user.sub.startsWith("email|")) {
      await linkAccounts();
      const res = await fetch(`${serverURL()}/user/user-by-email/${user.email}`, {
        method: "GET",
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message);
      }
      const userResponse = await res.json();
      userId = userResponse.userId;
    }

    const response = await fetch(`${serverURL()}/customers/${userId}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }
    const customer = await response.json();
    setCustomer(customer);
    return customer;
  };

  // this adds a css variable for elements that need to be 100 viewport height
  // 100vh is glitchy on mobile browsers, this is a solution to fix that

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);

    return () => {
      window.removeEventListener("resize", setVH);
      window.removeEventListener("orientationchange", setVH);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) {
        const cart = JSON.parse(localStorage.getItem("greyGullCart")) || [];
        setCartProducts(cart);
      }
      try {
        const response = await getProducts();
        const designs = await getDesigns();
        const configSettings = await getSettings();

        setAvailableProducts(response);
        setDesigns(designs);
        setConfiguration(configSettings);
      } catch (error) {
        console.error(error.message);
        toast.error(error.message);
      }
    };
    fetchData();
  }, []);

  function removeDuplicates(arr1, arr2) {
    const seen = {};
    const bothCarts = [...arr1, ...arr2];
    return bothCarts.reduce((unique, obj) => {
      let newArr = [...unique];
      const identifier = `${obj["size"]}-${obj["design"]}-${obj["position"]}`;
      if (!seen[identifier]) {
        seen[identifier] = true;
        newArr.push(obj);
      } else {
        const filteredArray = unique.filter((y) => {
          return !(obj.size === y.size && obj.design === y.design && obj.position === y.position);
        });
        const foundObj = unique.find((x) => {
          return obj.size === x.size && obj.design === x.design && obj.position === x.position;
        });
        const newObj = foundObj.quantity >= obj.quantity ? foundObj : obj;
        newArr = [...filteredArray, newObj];
      }
      return newArr;
    }, []);
  }

  useEffect(() => {
    if (isAuthenticated) {
      const fetchData = async () => {
        try {
          const loginTime = sessionStorage.getItem("login-time");
          const headers = await getAuthHeaders();
          await getCustomer();
          const cartLocalStorage = JSON.parse(localStorage.getItem("greyGullCart")) || [];
          const cartDatabase = await getCart(user?.sub, headers);
          const mergedCarts = removeDuplicates(cartLocalStorage, cartDatabase);
          if (!loginTime) {
            if (mergedCarts.length > 0) {
              // updates the cart when user logs in
              // combines database with localStorage
              if (cartDatabase.length > 0) {
                await patchCart(user.sub, mergedCarts, headers);
              } else {
                await createCart(user.sub, mergedCarts, headers);
              }
              updateCart(mergedCarts);
              localStorage.removeItem("greyGullCart");
            }
            sessionStorage.setItem("login-time", Date.now().toString());
          }
          setCartProducts(mergedCarts);
        } catch (error) {
          console.error(error);
          toast.error(error.message);
        }
      };
      fetchData();
    }
  }, [user?.sub]);

  function getProduct(id) {
    let product = availableProducts.find((p) => p.productId === id);
    if (product === undefined) {
      console.error("Product data does not exist for ID: " + id);
      return undefined;
    }
    return product;
  }

  function getMatchingCartProducts(id) {
    return cartProducts.filter((product) => {
      return product.productId === id;
    });
  }

  function saveUpdatedCart(cart) {
    setCartProducts(cart);
    if (!isAuthenticated) {
      localStorage.setItem("greyGullCart", JSON.stringify(cart));
    }
  }

  async function updateCart(cart) {
    let response = null;
    if (user?.sub) {
      const headers = await getAuthHeaders();
      if (cart.length === 0) {
        response = await deleteCart(user.sub, headers);
      } else if (cartProducts?._id || cartProducts.length > 0) {
        response = await patchCart(user.sub, cart, headers);
      } else {
        response = await createCart(user.sub, cart, headers);
      }
      if (response.ok) {
        saveUpdatedCart(cart);
      }
    } else {
      saveUpdatedCart(cart);
    }
    return response;
  }

  const getUpdatedQuantityValue = (itemId, quantity, fromCart = false) => {
    let updatedQuantity = null;
    if (!fromCart) {
      const foundProduct = cartProducts.find((cartProduct) => itemId === cartProduct.itemId);
      updatedQuantity = foundProduct.quantity + quantity;
    } else {
      updatedQuantity = quantity;
    }
    return updatedQuantity;
  };

  async function updateQuantity(itemId, quantity, fromCart = false) {
    let response = null;
    const updatedQuantity = getUpdatedQuantityValue(itemId, quantity, fromCart);
    const newCart = cartProducts.map((cartProduct) =>
      itemId === cartProduct.itemId ? { ...cartProduct, quantity: updatedQuantity } : cartProduct
    );

    if (user?.sub) {
      const headers = await getAuthHeaders();
      // update database
      response = await patchCartQuantity(user.sub, itemId, updatedQuantity, headers);
      if (response.ok) {
        saveUpdatedCart(newCart);
      }
    } else {
      saveUpdatedCart(newCart);
    }
    return response;
  }

  async function deleteFromCart(id) {
    const cart = (cartProducts) =>
      cartProducts.filter((currentProduct) => {
        if (currentProduct.itemId === id) {
          return false;
        } else {
          return true;
        }
      });

    updateCart(cart(cartProducts)).catch((error) => {
      console.error(error.message);
      toast.error(error.message);
    });
  }

  function clearCartState() {
    setCartProducts([]);
    localStorage.removeItem("greyGullCart");
  }

  function addToCart(productId, quantity, options) {
    const { priceId, cost } = getProduct(productId);
    const matchingProducts = getMatchingCartProducts(productId);
    // map through all products to see if there's a perfect match
    const existingProduct =
      matchingProducts.length > 0 &&
      matchingProducts.find((product) => {
        return product.size === options.size && product.position === options.position && product.design === options.design;
      });
    if (existingProduct) {
      let quantityToAdd = Number(quantity);
      if (quantityToAdd + existingProduct.quantity > 10) {
        toast("Max quantity reached for this product");
        quantityToAdd = 10 - existingProduct.quantity;
      }
      const response = updateExistingProductQuantity(existingProduct.itemId, quantityToAdd);
      return response;
    } else {
      const response = updateCart([
        ...cartProducts,
        {
          productId,
          itemId: Math.floor(Math.random() * 1000000000),
          priceId,
          quantity: Number(quantity),
          productCost: cost,
          ...options,
        },
      ]);
      return response;
    }
  }

  // used when adding product that already exists in the cart
  async function updateExistingProductQuantity(itemId, quantity) {
    const response = await updateQuantity(itemId, quantity);
    return response;
  }

  async function updateQuantityFromCart(itemId, quantity) {
    try {
      await updateQuantity(itemId, quantity, true);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message);
    }
  }

  function getSubTotal() {
    let subTotal = 0;
    if (cartProducts.length === 0 || availableProducts.length === 0) return 0;
    cartProducts.forEach((product) => {
      const productData = getProduct(product.productId);
      subTotal += productData?.cost * product.quantity;
    });
    return subTotal;
  }

  const contextValue = {
    customer,
    availableProducts,
    visitedPages: visited,
    addVisitedPage,
    productsInCart: cartProducts,
    getCustomer,
    getProduct,
    deleteFromCart,
    clearCartState,
    addToCart,
    updateQuantityFromCart,
    getSubTotal,
    designs,
    settings: configuration,
  };

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
}

export default DataProvider;
